<template>
  <div
    v-show="visible"
    class="layer_panel"
    :class="{ collapsed: !expanded, dragging: isDragging }"
    :style="{ left: `${position.x}px`, top: `${position.y}px` }"
  >
    <div
      class="layer_panel_header"
    >
      <div
        class="layer_panel_title"
        @touchstart.stop.prevent="handleDragStart"
        @touchmove.stop.prevent="handleDragMove"
        @touchend.stop.prevent="handleDragEnd"
        @mousedown.stop.prevent="handleDragStart"
      >
        <span>图层</span>
        <em>{{ layers.length }}</em>
      </div>
      <button class="layer_panel_toggle" type="button" @click.stop="toggleExpanded">
        {{ expanded ? "收" : "展" }}
      </button>
    </div>
    <div v-show="expanded" class="layer_panel_body">
      <div class="layer_panel_actions">
        <button type="button" class="panel_action icon_only" title="全部显示" @click="setAllVisible(true)">
          <van-icon name="eye-o" />
        </button>
        <button type="button" class="panel_action icon_only" title="全部隐藏" @click="setAllVisible(false)">
          <van-icon name="closed-eye" />
        </button>
        <button type="button" class="panel_action icon_only" title="全部锁定" @click="setAllLock(true)">
          <van-icon name="lock" />
        </button>
        <button type="button" class="panel_action icon_only" title="全部解锁" @click="setAllLock(false)">
          <van-icon name="passed" />
        </button>
      </div>
      <div v-if="displayLayers.length" ref="layerList" class="layer_list">
        <div
          v-for="item in displayLayers"
          :key="item.id"
          :ref="`layerItem-${item.id}`"
          class="layer_item"
          :class="{
            selected: item.selected,
            hidden: !item.visible,
            locked: item.locked,
          }"
          @click="selectLayer(item)"
        >
          <div class="layer_item_main">
            <div class="layer_item_index">{{ item.order }}</div>
            <div class="layer_item_name">{{ item.name }}</div>
          </div>
          <div class="layer_item_tools">
            <button
              type="button"
              class="layer_tool icon_only"
              :title="item.visible ? '隐藏' : '显示'"
              @click.stop="toggleVisible(item)"
            >
              <van-icon :name="item.visible ? 'eye-o' : 'closed-eye'" />
            </button>
            <button
              type="button"
              class="layer_tool icon_only"
              :title="item.locked ? '解锁' : '锁定'"
              @click.stop="toggleLock(item)"
            >
              <van-icon :name="item.locked ? 'passed' : 'lock'" />
            </button>
          </div>
        </div>
      </div>
      <div v-else class="layer_empty">还没有花材图层</div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      expanded: true,
      visible: true,
      layers: [],
      position: {
        x: 0,
        y: 110,
      },
      dragStart: {
        x: 0,
        y: 0,
        left: 0,
        top: 0,
      },
      isDragging: false,
      movedDuringDrag: false,
      mouseMoveHandler: null,
      mouseUpHandler: null,
    };
  },
  computed: {
    displayLayers() {
      return this.layers
        .slice()
        .reverse()
        .map((item, index) => ({
          ...item,
          order: this.layers.length - index,
        }));
    },
  },
  created() {
    this.$bus.$on("layer-change", (layers) => {
      this.layers = layers || [];
      this.$nextTick(() => {
        this.scrollToSelectedLayer();
      });
    });
    this.$bus.$on("layer-panel-toggle", () => {
      this.visible = !this.visible;
      this.emitVisibleChange();
    });
    this.$bus.$on("layer-panel-show", () => {
      this.visible = true;
      this.emitVisibleChange();
    });
    this.$bus.$on("layer-panel-hide", () => {
      this.visible = false;
      this.emitVisibleChange();
    });
  },
  mounted() {
    this.resetPosition();
    this.emitVisibleChange();
  },
  beforeDestroy() {
    if (this.mouseMoveHandler) {
      window.removeEventListener("mousemove", this.mouseMoveHandler);
    }
    if (this.mouseUpHandler) {
      window.removeEventListener("mouseup", this.mouseUpHandler);
    }
  },
  methods: {
    resetPosition() {
      const width = this.expanded ? 176 : 72;
      this.position.x = Math.max(window.innerWidth - width - 12, 8);
      this.position.y = 110;
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      this.$nextTick(() => {
        this.position.x = Math.min(
          this.position.x,
          window.innerWidth - this.panelWidth() - 8
        );
      });
    },
    emitVisibleChange() {
      this.$bus.$emit("layer-panel-visible-change", this.visible);
    },
    panelWidth() {
      return this.expanded ? 176 : 72;
    },
    panelHeight() {
      return this.expanded ? Math.min(window.innerHeight * 0.48, 360) : 44;
    },
    clampPosition(nextX, nextY) {
      const maxX = Math.max(window.innerWidth - this.panelWidth() - 8, 8);
      const maxY = Math.max(window.innerHeight - this.panelHeight() - 8, 8);
      this.position.x = Math.min(Math.max(8, nextX), maxX);
      this.position.y = Math.min(Math.max(8, nextY), maxY);
    },
    getPoint(event) {
      if (event.touches && event.touches.length) {
        return event.touches[0];
      }
      return event;
    },
    handleDragStart(event) {
      const point = this.getPoint(event);
      this.isDragging = true;
      this.movedDuringDrag = false;
      this.dragStart = {
        x: point.clientX,
        y: point.clientY,
        left: this.position.x,
        top: this.position.y,
      };
      if (!event.touches) {
        this.mouseMoveHandler = (moveEvent) => this.handleDragMove(moveEvent);
        this.mouseUpHandler = () => this.handleDragEnd();
        window.addEventListener("mousemove", this.mouseMoveHandler);
        window.addEventListener("mouseup", this.mouseUpHandler, { once: true });
      }
    },
    handleDragMove(event) {
      if (!this.isDragging) {
        return;
      }
      const point = this.getPoint(event);
      const deltaX = point.clientX - this.dragStart.x;
      const deltaY = point.clientY - this.dragStart.y;
      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
        this.movedDuringDrag = true;
      }
      this.clampPosition(
        this.dragStart.left + deltaX,
        this.dragStart.top + deltaY
      );
    },
    handleDragEnd() {
      this.isDragging = false;
      if (this.mouseMoveHandler) {
        window.removeEventListener("mousemove", this.mouseMoveHandler);
        this.mouseMoveHandler = null;
      }
      this.mouseUpHandler = null;
      window.setTimeout(() => {
        this.movedDuringDrag = false;
      }, 0);
    },
    selectLayer(item) {
      this.$bus.$emit("layer-select", item.id);
    },
    setAllVisible(visible) {
      this.$bus.$emit("layer-set-all-visible", visible);
    },
    setAllLock(locked) {
      this.$bus.$emit("layer-set-all-lock", locked);
    },
    toggleVisible(item) {
      this.$bus.$emit("layer-toggle-visible", item.id);
    },
    toggleLock(item) {
      this.$bus.$emit("layer-toggle-lock", item.id);
    },
    scrollToSelectedLayer() {
      const selectedLayer = this.displayLayers.find((item) => item.selected);
      if (!selectedLayer || !this.$refs.layerList) {
        return;
      }
      const targetRef = this.$refs[`layerItem-${selectedLayer.id}`];
      const target = Array.isArray(targetRef) ? targetRef[0] : targetRef;
      if (!target || !target.scrollIntoView) {
        return;
      }
      target.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.layer_panel {
  position: fixed;
  z-index: 40;
  width: 176px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 rem(12) rem(32) rgba(34, 52, 17, 0.18);
  backdrop-filter: blur(10px);
  overflow: hidden;
  transition: width 0.2s ease, opacity 0.2s ease;
}

.layer_panel.collapsed {
  width: 72px;
}

.layer_panel.dragging {
  opacity: 0.92;
}

.layer_panel_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: rem(18) rem(16);
  background: linear-gradient(135deg, #6c8f33, #8cb244);
  color: #fff;
  cursor: move;
}

.layer_panel_title {
  display: flex;
  align-items: center;
  gap: rem(10);
  font-size: rem(26);
  font-weight: 600;
}

.layer_panel_title em {
  display: inline-flex;
  min-width: rem(34);
  height: rem(34);
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  font-style: normal;
  font-size: rem(20);
}

.layer_panel_toggle {
  border: 0;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border-radius: 999px;
  padding: rem(6) rem(14);
  font-size: rem(20);
}

.layer_panel_body {
  padding: rem(12);
}

.layer_panel_actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: rem(8);
  margin-bottom: rem(10);
}

.panel_action,
.layer_tool {
  border: 0;
  border-radius: 999px;
  background: #edf3df;
  color: #4d6720;
  font-size: rem(18);
  padding: rem(8) rem(14);
}

.icon_only {
  width: rem(52);
  height: rem(52);
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: rem(26);
}

.layer_list {
  display: flex;
  flex-direction: column;
  gap: rem(8);
  max-height: 42vh;
  overflow-y: auto;
}

.layer_item {
  border-radius: 14px;
  border: 1px solid rgba(120, 150, 62, 0.12);
  background: #f9fbf4;
  padding: rem(10);
}

.layer_item.selected {
  border-color: #7ca336;
  box-shadow: inset 0 0 0 1px #7ca336;
  background: #eef6df;
}

.layer_item.hidden {
  opacity: 0.55;
}

.layer_item.locked {
  background: #f2f3ec;
}

.layer_item_main {
  display: flex;
  align-items: center;
  gap: rem(10);
}

.layer_item_index {
  flex: 0 0 rem(34);
  height: rem(34);
  border-radius: 50%;
  background: #dce8c0;
  color: #587622;
  font-size: rem(18);
  display: flex;
  align-items: center;
  justify-content: center;
}

.layer_item_name {
  flex: 1;
  min-width: 0;
  color: #344614;
  font-size: rem(22);
  line-height: 1.35;
  word-break: break-all;
}

.layer_item_tools {
  display: flex;
  justify-content: flex-end;
  gap: rem(8);
  margin-top: rem(10);
}

.layer_empty {
  padding: rem(26) rem(10);
  text-align: center;
  color: #7d876d;
  font-size: rem(22);
}
</style>
